/**
 * utils/sa_id_validator.js
 * Clean SA ID validator with Luhn + DOB + gender + citizenship
 */

function to_digit(value, index) {
  const char_code = value.charCodeAt(index) - 48;
  return char_code >= 0 && char_code <= 9 ? char_code : NaN;
}

function luhn_check(number_string) {
  let sum = 0;
  let double_digit = false;

  for (let i = number_string.length - 1; i >= 0; i--) {
    let digit = to_digit(number_string, i);

    if (double_digit) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    double_digit = !double_digit;
  }

  return sum % 10 === 0;
}

function get_two_digit_number(value, start_index) {
  return (to_digit(value, start_index) * 10) + to_digit(value, start_index + 1);
}

function is_valid_calendar_date(year, month, day) {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function resolve_birth_year(yy) {
  const current_year = new Date().getFullYear();
  const current_two_digit_year = current_year % 100;

  return yy <= current_two_digit_year
    ? 2000 + yy
    : 1900 + yy;
}

function has_invalid_birth_parts(yy, mm, dd) {
  return Number.isNaN(yy) || Number.isNaN(mm) || Number.isNaN(dd) || mm < 1 || mm > 12 || dd < 1 || dd > 31;
}

function create_valid_birth_date(yy, mm, dd) {
  const year = resolve_birth_year(yy);
  if (year === null) return null;
  if (!is_valid_calendar_date(year, mm, dd)) return null;
  return new Date(year, mm - 1, dd);
}

function parse_date_of_birth(yy_mm_dd) {
  const yy = get_two_digit_number(yy_mm_dd, 0);
  const mm = get_two_digit_number(yy_mm_dd, 2);
  const dd = get_two_digit_number(yy_mm_dd, 4);

  if (has_invalid_birth_parts(yy, mm, dd)) return null;
  return create_valid_birth_date(yy, mm, dd);
}

function format_date_for_input(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function validate_basic_format(cleaned, errors) {
  if (!cleaned) {
    errors.push("SA ID number is required.");
    return false;
  }

  if (!/^\d+$/.test(cleaned)) {
    errors.push("SA ID must contain digits only.");
  }

  if (cleaned.length !== 13) {
    errors.push("SA ID must be exactly 13 digits.");
  }

  return /^\d{13}$/.test(cleaned);
}

function get_gender_info(gender_part) {
  const gender_number = Number(gender_part);

  return {
    gender_number,
    gender: gender_number >= 5000 ? "Male" : "Female",
  };
}

function get_citizenship(citizenship_digit) {
  if (citizenship_digit === "0") return "South African citizen";
  if (citizenship_digit === "1") return "Permanent resident";
  return null;
}

function validate_detailed_rules(cleaned, errors) {
  const dob = parse_date_of_birth(cleaned.slice(0, 6));
  const { gender_number, gender } = get_gender_info(cleaned.slice(6, 10));
  const citizenship = get_citizenship(cleaned.charAt(10));

  if (!dob) errors.push("Invalid date of birth in first 6 digits (YYMMDD).");
  if (gender_number < 0 || gender_number > 9999) {
    errors.push("Digits 7-10 must be a valid 4-digit gender sequence.");
  }
  if (citizenship === null) {
    errors.push("Citizenship digit must be 0 (SA citizen) or 1 (Permanent resident).");
  }
  if (!luhn_check(cleaned)) {
    errors.push("SA ID checksum is invalid.");
  }

  return {
    gender,
    citizenship,
    date_of_birth: format_date_for_input(dob),
  };
}

export function validate_sa_id(id_number) {
  const cleaned = String(id_number || "").replace(/\s|-/g, "");
  const errors = [];
  const details = validate_basic_format(cleaned, errors)
    ? validate_detailed_rules(cleaned, errors)
    : { gender: null, citizenship: null, date_of_birth: null };
  const valid = errors.length === 0;
  var response = { valid, errors, ...details }

  return response;
}
